'use client';

import { getVisibleContentFields } from '@eliadmin/core/src/utils/content-fields';
import { contentFieldKey } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import { ContentFieldBlock } from '@eliadmin/core/src/components/content-fields/content-fields';
import { useEffect, useMemo, useState } from 'react';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { ProductTabsDescription } from './product-tabs-description';
import { ProductTabsParameters } from './product-tabs-parameters';
import { ProductTabsReviews } from './product-tabs-reviews';

type TabDef = {
  id: string;
  title: string;
  content: React.ReactNode;
  show: boolean;
};

export function ProductTabs({ product, locale }: { product: ProductDetailData; locale: string }) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const desiredObject = product.variants?.length ? selectedVariant : product;

  const { t } = useTranslations();

  const tabs = useMemo<TabDef[]>(
    () => [
      {
        id: 'description',
        title: t('text274'),
        content: <ProductTabsDescription product={product} selectedVariant={selectedVariant} />,
        show: !!desiredObject?.description,
      },
      {
        id: 'parameters',
        title: t('text2406'),
        content: <ProductTabsParameters product={product} selectedVariant={selectedVariant} />,
        show: !!(product?.attachments && product.attachments.length > 0),
      },
      {
        id: 'reviews',
        title: t('text447'),
        content: <ProductTabsReviews product={product} locale={locale} />,
        show: true,
      },

      ...getVisibleContentFields(product).map(field => ({
        id: contentFieldKey('products', field.id),
        key: contentFieldKey('products', field.id),
        name: field.name,
        title: field.name,
        content: <ContentFieldBlock field={field} module="products" />,
        show: true,
      })),
    ],
    [t, product, selectedVariant, desiredObject?.description, locale],
  );

  const visibleTabs = useMemo(() => tabs.filter(tab => tab.show), [tabs]);

  const [activeTab, setActiveTab] = useState<TabDef['id']>('description');

  useEffect(() => {
    const firstId = visibleTabs[0]?.id;
    if (!firstId) return;

    const isActiveStillVisible = visibleTabs.some(t => t.id === activeTab);
    if (!isActiveStillVisible) setActiveTab(firstId);
  }, [visibleTabs, activeTab]);

  if (visibleTabs.length === 0) return null;

  return (
    <section className={cn('custom-section', 'pb-24 pt-12')}>
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as TabDef['id'])}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 lg:gap-8',
        )}
      >
        <TabsList
          className={cn(
            'flex h-fit w-full items-center justify-start gap-4 whitespace-nowrap rounded-none bg-transparent sm:gap-5 lg:gap-6',
          )}
        >
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={cn(
                'whitespace-nowrap rounded-none p-0 font-oswald text-lg font-medium uppercase text-black sm:text-xl lg:text-2xl',
                'ea-product-tabs__tabs-trigger',
              )}
            >
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleTabs.map(tab => (
          <TabsContent key={tab.id} className="w-full" value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
